// Skeleton — reusable shimmer placeholder. Uses the existing `.skeleton` CSS
// class defined in index.css (subtle animated gradient). All variants expose
// `data-testid="skeleton"` so tests can wait for real content.
import { motion } from "framer-motion";

/**
 * Basic block skeleton. Pass any Tailwind size classes.
 * Example: <Skeleton className="h-6 w-40" />
 */
export function Skeleton({ className = "", rounded = "rounded-lg", ...rest }) {
  return (
    <div
      data-testid="skeleton"
      className={`skeleton ${rounded} ${className}`}
      {...rest}
    />
  );
}

/** Circle skeleton (avatar/icon placeholder). */
export function SkeletonCircle({ size = 32, className = "" }) {
  return (
    <div
      data-testid="skeleton"
      className={`skeleton rounded-full ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

/** Text lines skeleton — mimics a paragraph. */
export function SkeletonLines({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`} data-testid="skeleton-lines">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skeleton h-3 rounded-md"
          style={{ width: `${100 - i * 12}%` }}
        />
      ))}
    </div>
  );
}

/** Card-shaped skeleton with icon + title + description shape. */
export function SkeletonCard({ className = "" }) {
  return (
    <div className={`card p-5 ${className}`} data-testid="skeleton-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="skeleton w-11 h-11 rounded-xl" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-3.5 rounded-md w-3/5" />
          <div className="skeleton h-3 rounded-md w-2/5" />
        </div>
      </div>
      <SkeletonLines lines={3} />
    </div>
  );
}

/** Table-row skeleton — used inside table bodies while data loads. */
export function SkeletonRow({ cols = 4, className = "" }) {
  return (
    <div className={`grid gap-3 py-2.5 ${className}`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {Array.from({ length: cols }).map((_, i) => (
        <div key={i} className="skeleton h-4 rounded-md" style={{ width: `${100 - i * 6}%` }} />
      ))}
    </div>
  );
}

/**
 * Fade-in wrapper — pair with any skeleton to soften mount, so pages don't feel
 * abrupt if the API is fast (<100ms).
 */
export function FadeIn({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25, delay }}
    >
      {children}
    </motion.div>
  );
}

export default Skeleton;
